import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock } from "lucide-react";
import { CONTACT, TREATMENTS, AESTHETIC_TREATMENTS, LOCATIONS } from "@/data/site";
import { CtaPair } from "@/components/common/CtaButtons";

const quickLinks = [
  { label: "About", to: "/dr-ankesh-seth" },
  { label: "Dental Treatments", to: "/treatments" },
  { label: "Skin & Hair Treatments", to: "/aesthetic-treatments" },
  { label: "Dental Problems", to: "/problems" },
  { label: "Locations", to: "/locations" },
  { label: "Blog", to: "/blog" },
  { label: "Contact", to: "/contact" },
];

export const Footer = () => (
  <footer data-testid="site-footer" className="bg-brand-accent text-white">
    {/* CTA band */}
    <div className="mx-auto max-w-[1440px] px-6 pb-16 pt-24 md:px-12 lg:px-20">
      <div className="grid gap-10 border-b border-white/10 pb-16 lg:grid-cols-2 lg:items-end">
        <h2 className="font-display text-5xl font-semibold leading-[0.95] tracking-tight md:text-7xl">
          Ready to <span className="italic text-brand-green">Smile?</span>
        </h2>
        <div className="lg:justify-self-end">
          <p className="mb-5 max-w-md text-white/70">
            Book your appointment today. Our friendly team in Paharia is ready to give you the
            confident, healthy smile you deserve.
          </p>
          <CtaPair />
        </div>
      </div>

      <div className="grid gap-10 py-16 md:grid-cols-2 lg:grid-cols-4">
        <div>
          <span className="inline-flex items-center justify-center rounded-xl bg-white px-3 py-2.5">
            <img src="/nowtreat-logo.png" alt="NowTreat Dental, Skin & Hair Clinic" className="h-10 w-auto" width="71" height="40" />
          </span>
          <p className="mt-4 font-display text-xl">Dr. Ankesh Seth</p>
          <p className="mt-2 text-sm text-white/60">
            Advanced & painless dental care for Varanasi, delivered with honesty and modern
            technology.
          </p>
        </div>

        <div>
          <h4 className="mb-4 text-sm font-semibold uppercase tracking-[0.18em] text-white/50">
            Quick Links
          </h4>
          <ul className="space-y-2.5 text-sm">
            {quickLinks.map((l) => (
              <li key={l.to}>
                <Link to={l.to} className="text-white/75 transition-colors hover:text-brand-green">
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="mb-4 text-sm font-semibold uppercase tracking-[0.18em] text-white/50">
            Treatments
          </h4>
          <ul className="space-y-2.5 text-sm">
            {TREATMENTS.slice(0, 4).map((t) => (
              <li key={t.slug}>
                <Link
                  to={`/${t.slug}`}
                  className="text-white/75 transition-colors hover:text-brand-green"
                >
                  {t.name}
                </Link>
              </li>
            ))}
            {AESTHETIC_TREATMENTS.slice(0, 3).map((t) => (
              <li key={t.slug}>
                <Link
                  to={`/${t.slug}`}
                  className="text-white/75 transition-colors hover:text-brand-green"
                >
                  {t.name}
                </Link>
              </li>
            ))}
            {[["hair-fall-treatment-varanasi", "Hair Fall Treatment"], ["hair-transplant-varanasi", "Hair Transplant"]].map(([to, label]) => (
              <li key={to}>
                <Link to={`/${to}`} className="text-white/75 transition-colors hover:text-brand-green">
                  {label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="mb-4 text-sm font-semibold uppercase tracking-[0.18em] text-white/50">
            Contact
          </h4>
          <ul className="space-y-3 text-sm text-white/75">
            <li className="flex items-start gap-3">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-brand-green" />
              <span>{CONTACT.address}</span>
            </li>
            <li className="flex items-center gap-3">
              <Phone className="h-4 w-4 shrink-0 text-brand-green" />
              <a href={CONTACT.phoneTel} className="hover:text-brand-green">{CONTACT.phoneDisplay}</a>
            </li>
            <li className="flex items-center gap-3">
              <Mail className="h-4 w-4 shrink-0 text-brand-green" />
              <a href={`mailto:${CONTACT.email}`} className="break-all hover:text-brand-green">
                {CONTACT.email}
              </a>
            </li>
            <li className="flex items-start gap-3">
              <Clock className="mt-0.5 h-4 w-4 shrink-0 text-brand-green" />
              <span>
                {CONTACT.timings.map((t) => (
                  <span key={t.day} className="block">{t.day}: {t.hours}</span>
                ))}
              </span>
            </li>
          </ul>
        </div>
      </div>

      <div className="flex flex-col items-center justify-between gap-3 border-t border-white/10 pt-8 text-xs text-white/50 md:flex-row">
        <p>© {new Date().getFullYear()} Dr. Ankesh Seth Dental Clinic. All rights reserved.</p>
        <p className="flex flex-wrap gap-x-3">
          {LOCATIONS.map((l) => (
            <Link key={l.slug} to={`/${l.slug}`} className="hover:text-brand-green">
              Dentist in {l.area}
            </Link>
          ))}
        </p>
      </div>
    </div>
  </footer>
);
